import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { PublicLayout, SectionHeading, Testimonials } from "../components/PublicLayout";
import { ArrowRight, BadgeCheck, Banknote, ShoppingBag, FileText, ShieldCheck, TrendingUp, BookOpen } from "lucide-react";

const MARQUEE = ["Self Publishing", "Kindle eBook", "Paperback", "Hardcover", "Audiobooks", "100+ marketplaces", "Interior formatting", "Cover design", "Royalty tracking"];

const FloatCard = ({ icon: Icon, iconBg, title, value, className = "", testId, style }) => (
  <div data-testid={testId} style={style}
    className={`absolute bg-white/95 backdrop-blur-md rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-white/40 ${className}`}>
    <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}><Icon size={18} className="text-white" /></span>
    <div className="pr-1">
      <p className="text-[13px] font-semibold leading-tight">{title}</p>
      {value && <p className="text-xs text-[#52525B] mt-0.5">{value}</p>}
    </div>
  </div>
);

const PIPELINE_MOCK = ["Submitted", "Prep", "Design", "Formatting", "Live"];

export default function Landing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-14 text-center">
        <span className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold rounded-full px-4 py-1.5 mb-6 fade-up" data-testid="hero-badge">
          <ShieldCheck size={13} /> Publishing, handled with care
        </span>
        <h1 className="text-5xl sm:text-6xl lg:text-[4.5rem] font-bold tracking-tighter leading-none font-heading max-w-4xl mx-auto fade-up">
          Turn your manuscript into a <span className="text-teal-700">published book.</span>
        </h1>
        <p className="text-base sm:text-lg text-[#52525B] leading-relaxed mt-7 max-w-2xl mx-auto fade-up" style={{ animationDelay: "120ms" }}>
          Submit once. We design, format and self publish your book to Amazon — while you watch every stage and collect every royalty.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-9 fade-up" style={{ animationDelay: "240ms" }}>
          <Link to="/register" data-testid="landing-cta-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white px-8 py-3.5 font-medium hover:bg-[#27272A] transition-colors active:scale-95">
            Submit your manuscript <ArrowRight size={16} />
          </Link>
          <Link to="/store" data-testid="landing-store-button"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E4E4E7] px-8 py-3.5 font-medium hover:bg-[#F5F5F7] transition-colors active:scale-95">
            <ShoppingBag size={16} /> Browse the bookstore
          </Link>
          <Link to="/support" data-testid="landing-support-button"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-200 text-teal-800 px-8 py-3.5 font-medium hover:bg-teal-50 transition-colors active:scale-95">
            <ShieldCheck size={16} /> Submit a ticket
          </Link>
        </div>
      </section>

      {/* Hero image + floating micro-cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 fade-up" style={{ animationDelay: "360ms" }}>
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1800&q=80"
            alt="Author writing on a laptop" className="rounded-3xl w-full h-[340px] sm:h-[460px] object-cover shadow-sm" />
          <FloatCard testId="hero-card-royalty" icon={Banknote} iconBg="bg-emerald-500"
            title="Royalty payment received" value="+$142.50 to your balance"
            className="animate-floaty left-4 sm:left-10 top-6 sm:top-12" />
          <FloatCard testId="hero-card-live" icon={BadgeCheck} iconBg="bg-[#0055FF]"
            title="Your book is Live on Amazon" value="Stage 8 of 8 — congratulations!"
            className="animate-floaty-slow right-4 sm:right-12 top-20 sm:top-24" />
          <FloatCard testId="hero-card-sale" icon={ShoppingBag} iconBg="bg-[#0A0A0A]"
            title="3 copies sold in the GloriPub Store" value="Earnings credited instantly"
            className="animate-floaty left-6 sm:left-24 bottom-6 sm:bottom-10" style={{ animationDelay: "1s" }} />
        </div>
      </section>

      {/* Two products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="pathway-section">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-4 text-center">One platform, two products</p>
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/register" data-testid="pathway-publishing"
            className="group bg-[#F5F5F7] border border-[#E4E4E7] rounded-3xl p-10 hover:shadow-lg hover:-translate-y-1 transition-all">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">GloriPub Publishing</p>
            <h3 className="text-2xl font-bold font-heading mt-3">Publish your book</h3>
            <p className="text-sm text-[#52525B] mt-3 leading-relaxed">Manuscript to self-publishing — production pipeline, bookstore, royalties and marketing materials for authors.</p>
            <span className="inline-block mt-6 px-5 py-2 rounded-full bg-[#0A0A0A] text-white text-sm font-medium group-hover:bg-[#27272A]">Start publishing →</span>
          </Link>
          <Link to="/helpdesk" data-testid="pathway-helpdesk"
            className="group bg-[#0B1F33] text-white rounded-3xl p-10 hover:shadow-lg hover:-translate-y-1 transition-all">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-400">GloriDesk Helpdesk</p>
            <h3 className="text-2xl font-bold font-heading mt-3">Run your support desk</h3>
            <p className="text-sm text-white/60 mt-3 leading-relaxed">Enterprise-grade ticketing, SLAs, knowledge base and built-in remote screen sharing for your company.</p>
            <span className="inline-block mt-6 px-5 py-2 rounded-full bg-white text-[#0A0A0A] text-sm font-medium group-hover:bg-neutral-200">Explore helpdesk →</span>
          </Link>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-[#E4E4E7] py-5 overflow-hidden bg-white">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="mx-8 text-sm font-semibold uppercase tracking-[0.2em] text-[#A1A1AA] inline-flex items-center gap-8">
              {m} <span className="text-[#E4E4E7]">•</span>
            </span>
          ))}
        </div>
      </section>

      {/* Pipeline story + dashboard mock */}
      <section className="bg-[#F5F5F7] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA] mb-4">Transparent pipeline</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight font-heading">
              Watch every stage. Own every royalty.
            </h2>
            <p className="text-[#52525B] mt-5 leading-relaxed max-w-lg">
              Your dashboard shows exactly where your book is — from editorial review to the moment it goes live on Amazon. Then every sale, every marketplace and every dollar you've earned, in one place.
            </p>
            <Link to="/register" data-testid="pipeline-cta"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#0A0A0A] text-white px-8 py-3 font-medium hover:bg-[#27272A] transition-colors active:scale-95">
              Start your book <ArrowRight size={15} />
            </Link>
          </div>
          <div className="bg-white rounded-3xl border border-[#E4E4E7] p-8 shadow-sm" data-testid="dashboard-mock-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold font-heading text-lg">The Silent Harvest</p>
                <p className="text-xs text-[#A1A1AA] mt-0.5">Paperback & Kindle · Fiction</p>
              </div>
              <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1">Live on Amazon</span>
            </div>
            <div className="flex items-center gap-1.5 mt-6">
              {PIPELINE_MOCK.map((s, i) => (
                <div key={s} className="flex-1">
                  <div className={`h-1.5 rounded-full ${i < 5 ? "bg-[#0A0A0A]" : "bg-[#E4E4E7]"}`} />
                  <p className="text-[10px] text-[#A1A1AA] mt-1.5 hidden sm:block">{s}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-[#F5F5F7]">
              {[["Units sold", "1,284"], ["Your royalties", "$3,412.80"], ["Available balance", "$862.20"]].map(([k, v]) => (
                <div key={k}>
                  <p className="text-[11px] uppercase tracking-wider text-[#A1A1AA]">{k}</p>
                  <p className="font-bold font-heading text-lg sm:text-xl mt-1">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature trio */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <SectionHeading eyebrow="How it works" title="Publishing, handled for you" />
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: FileText, title: "Guided submission", desc: "Upload your manuscript and cover — or brief our designers. Pick a plan, pay once, and your book enters production." },
            { icon: ShieldCheck, title: "Professional production", desc: "Cover design, interior formatting and the publishing upload itself — every stage visible in real time, with a message thread to our team." },
            { icon: TrendingUp, title: "Sales, royalties & payouts", desc: "We import your official Amazon sales reports, apply your split transparently, and you withdraw your earnings whenever you like." },
          ].map((f, i) => (
            <div key={f.title} data-testid={`feature-card-${i}`}
              className="bg-white rounded-3xl border border-[#E4E4E7] p-8 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
              <span className="w-12 h-12 rounded-2xl bg-[#0A0A0A] flex items-center justify-center"><f.icon size={20} className="text-white" /></span>
              <h3 className="text-xl font-semibold font-heading mt-6">{f.title}</h3>
              <p className="text-sm text-[#52525B] mt-3 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Bookstore promo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid lg:grid-cols-2 gap-0 bg-[#0A0A0A] rounded-3xl overflow-hidden">
          <div className="p-10 sm:p-14 text-white flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-4">GloriPub Bookstore</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight font-heading">Buy directly from our authors.</h2>
            <p className="text-neutral-400 mt-4 leading-relaxed max-w-md">Every purchase in our store credits the author's royalty share instantly — no middlemen, no waiting.</p>
            <Link to="/store" data-testid="bookstore-promo-cta"
              className="inline-flex items-center gap-2 mt-8 w-fit rounded-full bg-white text-[#0A0A0A] px-8 py-3 font-medium hover:bg-neutral-200 transition-colors active:scale-95">
              <BookOpen size={16} /> Visit the store
            </Link>
          </div>
          <img src="https://images.unsplash.com/photo-1569728723358-d1a317aa7fba?auto=format&fit=crop&w=1200&q=80"
            alt="Books on a modern shelf" className="w-full h-72 lg:h-full object-cover" />
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <SectionHeading eyebrow="Pricing" title="One fee to publish. A small membership to stay." sub="One-time publishing fee plus a small monthly membership after your first 30 days." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((p, i) => (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`relative rounded-3xl p-8 bg-white transition-all hover:shadow-lg hover:-translate-y-0.5 border ${i === 1 ? "border-[#0A0A0A] shadow-md" : "border-[#E4E4E7] shadow-sm"}`}>
              {i === 1 && <span className="absolute -top-3 left-8 bg-[#0A0A0A] text-white text-xs font-semibold px-3 py-1 rounded-full">Most popular</span>}
              <h3 className="font-semibold font-heading text-lg">{p.name}</h3>
              {p.custom ? (
                <>
                  <p className="text-4xl font-bold mt-3 font-heading tracking-tight">${p.monthly_price}<span className="text-base font-medium text-[#A1A1AA]">/mo</span></p>
                  <p className="text-xs text-[#A1A1AA] mt-1.5">starting rate — agreed with your publisher</p>
                </>
              ) : (
                <>
                  <p className="text-4xl font-bold mt-3 font-heading tracking-tight">${p.price}</p>
                  <p className="text-xs text-[#A1A1AA] mt-1.5">one-time{p.monthly_price ? ` + $${p.monthly_price}/mo membership (first 30 days included)` : ""}</p>
                </>
              )}
              <ul className="mt-6 space-y-2.5">
                {(p.features || []).map((f) => (
                  <li key={f} className="text-sm text-[#52525B] flex gap-2.5"><span className="text-emerald-600 font-bold">✓</span>{f}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Link to="/pricing" className="inline-flex items-center gap-2 text-sm font-medium mt-10 hover:gap-3 transition-all">
          Compare plans in detail <ArrowRight size={14} />
        </Link>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F5F5F7] border-y border-[#E4E4E7] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Testimonials" title="Authors who trusted us with their words" />
          <Testimonials />
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight font-heading max-w-3xl mx-auto">
          Ready to see your book on Amazon?
        </h2>
        <p className="text-[#52525B] mt-5 max-w-xl mx-auto leading-relaxed">Join the authors publishing smarter with GloriPub — from finished manuscript to live listing, with royalties you can actually see.</p>
        <Link to="/register" data-testid="final-cta-button"
          className="inline-flex items-center gap-2 mt-9 rounded-full bg-[#0A0A0A] text-white px-9 py-4 font-medium hover:bg-[#27272A] transition-colors active:scale-95">
          Get started today <ArrowRight size={16} />
        </Link>
      </section>
    </PublicLayout>
  );
}
